import axios from 'axios';
import {
	httpErrorResponse as responseOf,
	httpErrorStatus,
	isNestErrorBody as isApiErrorBody,
	isNestMissingRouteError,
} from 'features/common/helpers/httpErrors';

/**
 * Lo que se muestra cuando el módulo de la Support API no está prendido en el
 * ambiente (`ENABLE_CARD_DESK` sin definir: sus rutas no existen) o le falta
 * una base. Es un estado esperado, no un error: en producción la mesa nace
 * apagada.
 */
export const CARD_DESK_NOT_ENABLED_MESSAGE = 'La mesa de cruces todavía no está habilitada en este ambiente.';

/**
 * ¿La API dice que la mesa no está habilitada?
 *
 * - **404** de una ruta que no existe (`Cannot GET /api/…`): con
 *   `ENABLE_CARD_DESK` apagado, `/api/data/card-desk/*` no existe. Otro 404 de
 *   la API ("La tarjeta 7 no está en la mesa") no quiere decir que esté apagada.
 * - **503** de la API: el módulo está, pero le falta la conexión al Power o a
 *   autorizaciones (`/crossability`, `/cards` y `/board`).
 *
 * Un 404 o 503 sin la forma de la API (el proxy con la API caída) no cuenta: es
 * un error y se reintenta. Vale solo para las LECTURAS. En una escritura, un 404
 * quiere decir otra cosa ("La regla 7 no existe") y se muestra con
 * `extractCardDeskError`.
 */
export function isCardDeskNotEnabledError(err: unknown): boolean {
	const response = responseOf(err);
	if (!response || !isApiErrorBody(response.status, response.data)) return false;
	return response.status === 503 || isNestMissingRouteError(err);
}

/**
 * ¿La API dice que lo pedido no está en la mesa ("La emisión … no está en la
 * mesa…")? Es su 404 con su mensaje: no el de una ruta que no existe (la mesa
 * apagada) ni el del proxy con la API caída.
 */
export function isCardDeskNotFoundError(err: unknown): boolean {
	const response = responseOf(err);
	if (!response || response.status !== 404 || !isApiErrorBody(404, response.data)) return false;
	return !isCardDeskNotEnabledError(err);
}

/**
 * ¿La falla no es de lo que se mandó, sino de la red, del servidor (5xx) o de la sesión (401 o 403, o sin credenciales:
 * no llega a salir)? Otro 4xx es un rechazo de ese pedido en particular.
 */
export function isOutageError(err: unknown): boolean {
	const status = responseOf(err)?.status;
	return status === undefined || status >= 500 || status === 401 || status === 403;
}

/** El estado HTTP de un error de axios; `null` sin respuesta. */
export const errorStatus = httpErrorStatus;

/** ¿Es un 401 o un 403: la sesión venció o el usuario no tiene permiso? */
export function isSessionError(err: unknown): boolean {
	const status = responseOf(err)?.status;
	return status === 401 || status === 403;
}

/** El `message` de un error de Nest: texto, o lista de textos si vino del ValidationPipe. */
function messageOf(data: unknown): string | null {
	if (!data || typeof data !== 'object') return null;
	const { message } = data as { message?: unknown };
	if (typeof message === 'string' && message.trim()) return message.trim();
	if (Array.isArray(message)) {
		const parts = message.filter((m): m is string => typeof m === 'string' && m.trim() !== '');
		if (parts.length > 0) return parts.join(' · ');
	}
	return null;
}

/**
 * Mensaje legible de un error de la mesa. Los 400, 403 y 404 de este módulo
 * traen mensajes pensados para quien usa el Power ("Tu rol no permite cambiar
 * las reglas…", "La regla no es válida: …"), así que se muestran tal cual.
 * Un 401 no desloguea: solo se avisa. Un 404 o 503 que no viene de la API dice
 * que la API no está respondiendo.
 */
export function extractCardDeskError(err: unknown): string {
	if (axios.isAxiosError(err)) {
		const status = err.response?.status;
		const data = err.response?.data;
		if ((status === 404 || status === 503) && !isApiErrorBody(status, data)) {
			return `La Support API no está respondiendo (${status}) — intentá de nuevo en un rato`;
		}
		const fromApi = messageOf(data);
		if (status && [400, 403, 404, 409, 422].includes(status)) {
			return fromApi ?? `Error ${status}`;
		}
		if (status === 401) return 'Tu sesión expiró — volvé a iniciar sesión';
		if (status === 503) return CARD_DESK_NOT_ENABLED_MESSAGE;
		if (status && status >= 500) return `Error del servidor (${status}) — intentá de nuevo`;
		if (err.request) return 'No se pudo conectar con el servidor — verificá tu conexión';
	}
	if (err instanceof Error && err.message) return err.message;
	return 'Error inesperado';
}

/** Un error de core (el backend del Power, no la Support API): su `message`, o que se pruebe de nuevo. */
export function extractCoreError(err: unknown): string {
	return (axios.isAxiosError(err) && messageOf(err.response?.data)) || 'Probá de nuevo en un rato.';
}
